import { useTable, useTranslate } from "@refinedev/core";
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
} from "@mui/lab";
import {
  Box,
  Typography,
  Chip,
  Stack,
  useTheme,
  Skeleton,
  alpha
} from "@mui/material";
import {
  NotificationsNone,
  Schedule,
  PersonAdd,
  DirectionsCar,
  Warning
} from "@mui/icons-material";
import { Activity } from "lucide-react";
import dayjs from "dayjs";
import { IUser } from "../../pages/user/UserList";
import React from "react";
import "dayjs/locale/fr";

interface INotification {
  createdAt: string;
  message: {
    en: string;
    fr: string;
    es: string;
    it: string;
    pt: string;
  };
  sender: IUser;
  data: any;
}

export const NotificationTimeline: React.FC = () => {
  const theme = useTheme();
  const translate = useTranslate();
  const { tableQueryResult } = useTable<INotification>({
    resource: "notifications/timeline",
    initialSorter: [
      {
        field: "createdAt",
        order: "desc",
      },
    ],
    initialPageSize: 20,
    syncWithLocation: false,
    hasPagination: false,
  });

  const isLoading = tableQueryResult?.isLoading;
  const notifications = tableQueryResult?.data?.data || [];

  // Function to extract status from notification data
  const getNotificationStatus = (data: any) => {
    // For alerts, try to extract status from data or default to 'NEW'
    if (data?.notificationType === "alert") {
      return data?.status || data?.alertStatus || 'NEW';
    }
    // For journeys, use 'ACCEPTED' to indicate active/in-progress
    else if (data?.journeyId) {
      return 'ACCEPTED';
    }
    // For other notification types, use 'CLOSED' as default
    else {
      return 'CLOSED';
    }
  };

  // Enhanced status-based styling function similar to MonitoringList
  const getStatusInfo = (status: string, theme: any) => {
    const isDark = theme.palette.mode === 'dark';

    switch (status) {
      case 'NEW':
        return {
          color: 'info',
          label: translate('monitoring.status.new'),
          bgColor: isDark ? theme.palette.info.dark : '#e3f2fd',
          borderColor: theme.palette.info.main
        };
      case 'ACCEPTED':
        return {
          color: 'warning',
          label: translate('monitoring.status.accepted'),
          bgColor: isDark ? theme.palette.warning.dark : '#fff3e0',
          borderColor: theme.palette.warning.main
        };
      case 'CLOSED':
        return {
          color: 'success',
          label: translate('monitoring.status.closed'),
          bgColor: isDark ? theme.palette.success.dark : '#e8f5e9',
          borderColor: theme.palette.success.main
        };
      case 'CALLED':
        return {
          color: 'secondary',
          label: translate('monitoring.status.called'),
          bgColor: isDark ? theme.palette.secondary.dark : '#f3e5f5',
          borderColor: theme.palette.secondary.main
        };
      default:
        return {
          color: 'default',
          label: status,
          bgColor: isDark ? theme.palette.grey[800] : '#f5f5f5',
          borderColor: theme.palette.grey[500]
        };
    }
  };

  // Function to get icon based on notification type
  const getNotificationIcon = (data: any) => {
    if (data?.notificationType === "alert") {
      return <Warning fontSize="small" />;
    } else if (data?.journeyId) {
      return <DirectionsCar fontSize="small" />;
    } else if (data?.type === "user_added") {
      return <PersonAdd fontSize="small" />;
    } else {
      return <Schedule fontSize="small" />;
    }
  };

  // Loading skeleton component
  const LoadingSkeleton = () => (
    <Box sx={{ px: 3, py: 2 }}>
      {[1, 2, 3].map((index) => (
        <Box key={index} sx={{ display: "flex", gap: 2, mb: 2 }}>
          <Skeleton variant="circular" width={40} height={40} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="30%" />
            <Skeleton variant="text" width="80%" />
            <Skeleton variant="text" width="60%" />
          </Box>
        </Box>
      ))}
    </Box>
  );

  return (
    <Box
      sx={{
        height: "100%",
        background: theme.palette.mode === "dark"
          ? "linear-gradient(135deg, #2d3748 0%, #4a5568 100%)"
          : "linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)",
        // borderRadius: 2,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <Stack sx={{ flexShrink: 0, p: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Activity size={32} color={theme.palette.primary.main} />
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.primary,
              fontWeight: 600,
              fontFamily: 'Inter, sans-serif'
            }}
          >
            {translate('dashboard.timeline', 'Recent Activities')}
          </Typography>
        </Stack>
      </Stack>

      {isLoading ? (
        <LoadingSkeleton />
      ) : (
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            minHeight: 0,
            px: 3,
          }}
        >
          {notifications.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                py: 4,
                textAlign: 'center'
              }}
            >
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  background: alpha(theme.palette.primary.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2
                }}
              >
                <NotificationsNone
                  sx={{
                    fontSize: 32,
                    color: theme.palette.primary.main
                  }}
                />
              </Box>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  mb: 1
                }}
              >
                {translate('dashboard.no_activities', 'Aucune activité récente')}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  maxWidth: 250
                }}
              >
                {translate('dashboard.activities_will_appear', 'Les activités de vos utilisateurs apparaîtront ici')}
              </Typography>
            </Box>
          ) : (
            <Timeline
              position="right"
              sx={{
                mt: 0,
                pt: 0,
                width: "100%",
                "& .MuiTimeline-root": {
                  maxWidth: "100%",
                },
                "& .MuiTimelineItem-root": {
                  minHeight: "auto",
                  "&:before": {
                    display: "none",
                  },
                },
              }}
            >
              {notifications.map(
                ({ createdAt, message, sender, data }, index) => {
                  const notificationStatus = getNotificationStatus(data);
                  const statusInfo = getStatusInfo(notificationStatus, theme);
                  const icon = getNotificationIcon(data);

                  return (
                    <TimelineItem key={index}>
                      <TimelineOppositeContent sx={{ display: "none" }} />
                      <TimelineSeparator>
                        <TimelineDot
                          sx={{
                            backgroundColor: statusInfo.borderColor,
                            color: theme.palette.getContrastText(statusInfo.borderColor),
                            boxShadow: `0 0 0 4px ${alpha(statusInfo.borderColor, 0.2)}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {icon}
                        </TimelineDot>
                        {index < notifications.length - 1 && (
                          <TimelineConnector
                            sx={{
                              backgroundColor: alpha(theme.palette.divider, 0.3),
                              width: "2px"
                            }}
                          />
                        )}
                      </TimelineSeparator>
                      <TimelineContent
                        sx={{
                          maxWidth: "calc(100% - 32px)",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            backgroundColor: alpha(statusInfo.bgColor, 0.9),
                            border: `1px solid ${alpha(statusInfo.borderColor, 0.3)}`,
                            borderRadius: 2,
                            p: 2,
                            wordWrap: "break-word",
                            maxWidth: "100%",
                            overflow: "hidden",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              boxShadow: `0 4px 12px ${alpha(statusInfo.borderColor, 0.2)}`,
                              transform: "translateX(2px)",
                              borderColor: statusInfo.borderColor,
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              mb: 1,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                color: theme.palette.text.secondary,
                                fontWeight: 500,
                              }}
                            >
                              {dayjs(createdAt).format("DD/MM/YYYY HH:mm")}
                            </Typography>

                            <Chip
                              label={statusInfo.label}
                              color={statusInfo.color as any}
                              size="small"
                              sx={{
                                fontWeight: 600,
                                fontSize: '0.7rem'
                              }}
                            />
                          </Box>

                          <Typography
                            variant="subtitle2"
                            sx={{
                              wordWrap: "break-word",
                              overflow: "hidden",
                              display: "-webkit-box",
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: "vertical",
                              color: theme.palette.text.primary,
                              fontWeight: 600,
                              mb: 0.5,
                            }}
                          >
                            {sender.fullname || translate('dashboard.unknown_user', 'Utilisateur inconnu')}
                          </Typography>

                          {message && (
                            <Typography
                              variant="caption"
                              sx={{
                                wordWrap: "break-word",
                                overflow: "hidden",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                color: theme.palette.text.secondary,
                              }}
                            >
                              {message.fr || message.en}
                            </Typography>
                          )}
                        </Box>
                      </TimelineContent>
                    </TimelineItem>
                  );
                }
              )}
            </Timeline>
          )}
        </Box>
      )}
    </Box>
  );
};
